/**
 * Pulse wall — L1 breakdown (the drill into one bottleneck). Extracted from
 * `pulse-wall.tsx` (#1144), ported from the approved `_zoom.tsx`
 * (`BreakdownLevel` / `InstanceLevel`).
 *
 * Fetches `pulse.breakdown`. PORTFOLIO scope → one bar per subject, worst-first
 * ("who is dragging it down"), each a real `/o/<uid>` Object-page navigation.
 * SINGLE-SUBJECT scope → the component rows (the checks behind the number). A
 * below-target bar carrying department-matched suggestions surfaces them as the
 * one-click <PrescriptionPanel> fix rows (§9.2). The breadcrumb (<Crumb>/<Sep>)
 * is exported so the navigator's section-dive crumb (pulse-wall.tsx) reuses it.
 */

import React, { useState, useTransition } from "react";
import { useNavigate as useRouter } from "../../router";
import { useSWRConfig } from "swr";
import { Card, CardContent } from "../../card";
import { Icon } from "../../icon";
import { Button } from "../../button";
import { Link } from "../link";
import { cn } from "../../utils";
import { formatDate } from "../../format";
import { apiCall, apiKey, useApi } from "../../api/client";
// RO-3-02 / RO-4-04: additive subpath, not the bare barrel (`pulse/types.ts`
// has zero imports of its own) — see `path-label.ts` for the fuller note.
import { PRIMARY_VERB, KIND_ICON } from "@excellent/core/pulse-types";
import { SuggestionRow, SUGGESTION_KIND_LABEL } from "../suggestions/suggestion-panel";
import { band, BAND, modeOfScope, pulseBarFill, pulseToneRgb, type Band } from "./pulse-wall-shared";
import {
  pulse,
  type PulseWeight,
  type PulseDrillEntityDTO,
  type PulseSuggestionDTO,
} from "../../api/contracts/pulse";

// ── breadcrumb ────────────────────────────────────────────────────────────────
//
// The drill location (`<group> › <touch>`) is NO LONGER rendered inline here —
// it rides the real top-nav breadcrumb trail (`Excellent / Work / Queue /
// Throughput`, via `deriveNavTrail` reading `?g`/`?t`), so the breakdown card
// doesn't duplicate it. `Crumb`/`Sep` stay exported for the navigator's
// section-dive crumb (pulse-wall.tsx "All sections › …"), which has no top-trail
// equivalent yet.

/** One breadcrumb crumb — a button when it climbs, plain text at the current level.
 *  Exported so the navigator's section-dive crumb (pulse-wall.tsx) reuses it. */
export function Crumb({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-sm px-1.5 py-0.5 type-subtext text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
    >
      {children}
    </button>
  ) : (
    <span className="px-1.5 py-0.5 type-subtext text-foreground">{children}</span>
  );
}

export const Sep = () => <Icon name="chevron-right" size="xs" className="text-muted-foreground/40" />;

/** SVG line/area colors per band (the `fill-`/`stroke-` utilities `<polygon>`/
 *  `<polyline>` need — distinct from BAND's `bg`/`fg` text-utility tokens). */
const BAND_CHART: Record<Band, { line: string; fill: string }> = {
  ok: { line: "text-success", fill: "fill-success/10" },
  watch: { line: "text-warning", fill: "fill-warning/10" },
  risk: { line: "text-danger", fill: "fill-danger/10" },
};

/** A point in a temporal breakdown — a `YYYY-MM-DD` sample with its health and
 *  the raw component label (carries the underlying count, e.g. "2026-06-13 (1)"). */
interface SeriesPoint {
  on: string;
  label: string;
  health: number;
}

/**
 * A breakdown whose component labels are ALL dates (`YYYY-MM-DD …`) is a time
 * SERIES, not a set of named checks — e.g. Throughput's per-day completed
 * counts. Rendering it through `ComponentRows` (sorted worst-first) scrambles
 * the chronology into a meaningless value-ranked bar list. Detect that case and
 * return the points sorted chronologically so they can render as a real trend
 * line; return null for a non-temporal breakdown (task titles, checks, …), which
 * keeps the value-sorted bars.
 */
function asDateSeries(components: { label: string; health: number }[]): SeriesPoint[] | null {
  if (components.length < 2) return null;
  const points: SeriesPoint[] = [];
  for (const c of components) {
    const m = c.label.match(/^(\d{4}-\d{2}-\d{2})/);
    if (!m) return null; // any non-date label ⇒ not a temporal series
    points.push({ on: m[1]!, label: c.label, health: c.health });
  }
  return points.sort((a, c) => a.on.localeCompare(c.on));
}

// ── L1 — the breakdown of one bottleneck (ported `_zoom.tsx` BreakdownLevel) ───
//
// Fetches `pulse.breakdown`. PORTFOLIO scope → one bar per subject, worst-first
// ("who is dragging it down"), each a real `/o/<uid>` Object-page navigation.
// SINGLE-SUBJECT scope → the component rows (the checks behind the number, like
// `_zoom` InstanceLevel's steps/checks). A batch verb row spans every at-risk
// subject (stubbed for P2; P3/P9 wire the prescriptions).

const TRACK1 = 150;

export function PulseBreakdown({
  scope,
  touchId,
  weight,
  days,
  onClimb,
}: {
  scope: string;
  touchId: string;
  weight?: PulseWeight;
  days?: number;
  onClimb: () => void;
}) {
  const { data, error, isLoading } = useApi(pulse.breakdown, {
    params: { scope, touch: touchId },
    query: { weight, days },
  });

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Card>
          <CardContent className="py-10 text-center type-body text-muted-foreground">
            Loading breakdown…
          </CardContent>
        </Card>
      </div>
    );
  }

  const touch = data?.touch ?? null;
  // An empty/missing touch (unregistered provider, stale deep-link) → climb back.
  if (error || !touch || touch.health == null) {
    return (
      <div className="space-y-3">
        <Card>
          <CardContent className="py-10 text-center type-body text-muted-foreground">
            Nothing to break down here.{" "}
            <button type="button" onClick={onClimb} className="text-primary hover:underline">
              Back to the wall
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const group = data!.group;
  const perEntity = data!.perEntity;
  const components = data!.components;
  const mean = touch.health;
  const b = band(mean, touch.target);
  const kindIcon = KIND_ICON[touch.kind] ?? "activity";

  // Single-subject scope renders the component breakdown; portfolio renders the
  // per-entity bars. (When neither is present the breakdown is just the header.)
  const isSingleSubject = !!components && perEntity.length <= 1;
  // A date-labeled component breakdown is a TIME SERIES (e.g. Throughput's
  // per-day counts) — draw it as a chronological trend line, not value-sorted bars.
  const series = isSingleSubject ? asDateSeries(components!) : null;
  const subhead = series
    ? "— over time"
    : isSingleSubject
      ? "— the checks behind the number"
      : "— who is dragging it down";

  return (
    <div className="space-y-3">
      <Card className={`border ${BAND[b].border}`}>
        <CardContent className="p-0">
          <div className="px-5 pt-4">
            <div className="flex items-center gap-2">
              <Icon name={kindIcon} size="sm" className={BAND[b].fg} />
              <span className="type-body font-medium">{touch.label}</span>
              <span className="type-subtext text-muted-foreground">{subhead}</span>
            </div>
          </div>

          {series ? (
            <ComponentTrend series={series} target={touch.target} chartBand={b} />
          ) : isSingleSubject ? (
            <ComponentRows components={components!} target={touch.target} />
          ) : (
            <PerEntityTrack perEntity={perEntity} />
          )}
        </CardContent>
      </Card>

      {/* §9.2 — the wall PRESCRIBES the fix: when this below-target bar carries
          department-matched suggestions (from the existing `sectionAgentSuggestions`
          engine, bound server-side), surface them as one-click fix rows. */}
      {touch.suggestions && touch.suggestions.length > 0 && (
        <PrescriptionPanel
          scope={scope}
          touchId={touchId}
          groupKey={group.key}
          touchLabel={touch.label}
          suggestions={touch.suggestions}
        />
      )}

      {/* batch over every at-risk subject on this bottleneck (P2 stubs; P3/P9
          wire the real prescriptions — flow-activate / agent-deploy / Play). */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          <span className="mr-1 type-subtext text-muted-foreground">
            batch · {touch.atRisk} at-risk
          </span>
          <Button size="sm" disabled>
            <Icon name="sparkles" size="sm" />
            {isSingleSubject ? "Fix all failing checks" : "Assign agent to all at-risk"}
          </Button>
          <Button variant="outline" size="sm" disabled>
            {touch.kind === "automation" && <Icon name="play" size="sm" />}
            {PRIMARY_VERB[touch.kind] ?? "Open"} for all
          </Button>
          <Button variant="ghost" size="sm" disabled>
            Export list
            <Icon name="arrow-right" size="sm" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── §9.2 fix rows — the wall's remedy, one-click ──────────────────────────────
//
// REUSES the `SuggestionRow` primitive (the same row the home Play queue + the
// Agents panel's "Suggested agents & flows" render), so a fix reads identically
// to every other "here's what's worth doing" surface: the flow's own name as the
// title, its kind ("Flow"/"Agent") in the accent slot, a one-line benefit + state
// chip below. The verb lives once — on the button (which POSTs `pulse.prescribe`,
// or links out when there's no real activation path), then revalidates the wall.

function PrescriptionPanel({
  scope,
  touchId,
  groupKey,
  touchLabel,
  suggestions,
}: {
  scope: string;
  touchId: string;
  groupKey: string;
  touchLabel: string;
  suggestions: PulseSuggestionDTO[];
}) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-col gap-0.5">
          <span className="type-body font-medium text-foreground">Move {touchLabel} faster</span>
          <span className="type-subtext text-muted-foreground">
            Ranked moves that should shift this metric — with the reason each surfaced.
          </span>
        </div>
        <div className="flex flex-col gap-2.5">
          {suggestions.map((s, i) => (
            <PrescriptionChip
              key={s.id}
              rank={i + 1}
              scope={scope}
              touchId={touchId}
              groupKey={groupKey}
              suggestion={s}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PrescriptionChip({
  rank,
  scope,
  touchId,
  groupKey,
  suggestion,
}: {
  rank: number;
  scope: string;
  touchId: string;
  groupKey: string;
  suggestion: PulseSuggestionDTO;
}) {
  const { mutate } = useSWRConfig();
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A suggestion with a real activation kind (flow/agent/play) turns on in one
  // click; otherwise (a `tool` nudge, or any kind that only links out) the row
  // follows its cta href. The accent shows the kind, matching every surface.
  const deployable = suggestion.kind === "flow" || suggestion.kind === "agent" || suggestion.kind === "play";
  const linkHref = suggestion.cta?.href;
  const detail = [suggestion.rationale, suggestion.evidence].filter(Boolean).join(" · ") || undefined;
  const kindLabel = SUGGESTION_KIND_LABEL[suggestion.kind];
  // Button copy: the verb lives here, once. "Turn on" → "Turning on…" → "On" for
  // the common activate; a generic busy/done for the "Optimize"/link-out labels.
  const activeLabel = suggestion.cta?.label ?? "Turn on";
  const busyLabel = activeLabel === "Turn on" ? "Turning on…" : "Working…";
  const doneLabel = activeLabel === "Turn on" ? "On" : "Done";

  const deploy = () => {
    if (pending) return;
    setError(null);
    startTransition(async () => {
      try {
        const res = await apiCall(pulse.prescribe, {
          body: {
            kind: suggestion.kind as "flow" | "agent" | "play",
            mode: modeOfScope(scope),
            groupKey,
            touchId,
            title: suggestion.title,
            ref: suggestion.ref,
            evidence: suggestion.evidence,
          },
        });
        if (!res.ok) {
          // A risky/uncertain wiring falls back to the surface it returned.
          if (res.href) router.push(res.href);
          else setError("Couldn't turn it on — try again.");
          return;
        }
        setDone(res.outcome);
        // The wall changes once the flow is live / the action is queued.
        await mutate(apiKey(pulse.aggregate, { params: { scope }, query: {} }));
        await mutate(apiKey(pulse.breakdown, { params: { scope, touch: touchId }, query: {} }));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't turn it on.");
      }
    });
  };

  // A one-click suggestion gets the row + a Button beside it (the verb lives on
  // the button); a link-only one is a plain SuggestionRow that follows its href.
  if (!deployable && linkHref) {
    return (
      <SuggestionRow
        rank={rank}
        title={suggestion.title}
        accent={kindLabel}
        detail={detail}
        href={linkHref}
      />
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <SuggestionRow rank={rank} title={suggestion.title} accent={kindLabel} detail={detail} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <Button size="sm" disabled={pending || done != null} onClick={deploy}>
          {pending && <Icon name="loader2" size="sm" className="animate-spin" />}
          {pending ? busyLabel : done ? doneLabel : activeLabel}
        </Button>
        {error && <span className="type-subtext text-destructive">{error}</span>}
      </div>
    </div>
  );
}

/** PORTFOLIO L1 — one bar per subject, worst-first, value labels above bars,
 *  each a real `/o/<uid>` navigation (the L2 drill, server-computed `href`). */
function PerEntityTrack({ perEntity }: { perEntity: PulseDrillEntityDTO[] }) {
  if (!perEntity.length) {
    return (
      <div className="px-5 pb-5 pt-4 text-center type-subtext text-muted-foreground">
        No subject is instrumented for this touchpoint.
      </div>
    );
  }
  return (
    <>
      <div className="flex items-end gap-2 px-5 pb-2 pt-4" style={{ height: TRACK1 }}>
        {perEntity.map((p) => {
          const cfill = pulseBarFill(p.health, 70);
          const bar = (
            <>
              <span
                className="relative w-full rounded-xs transition-[filter] duration-150 group-hover/bar:brightness-110"
                style={{ height: `${Math.max(p.health, 5)}%`, background: cfill.background, boxShadow: cfill.boxShadow }}
              />
              <span
                className="absolute inset-x-0 text-center type-subtext tabular-nums text-foreground/90"
                style={{ bottom: `calc(${Math.max(p.health, 5)}% + 3px)` }}
              >
                {p.health}
              </span>
            </>
          );
          const cls = "group/bar relative flex h-full flex-1 items-end";
          const tip = `${p.entity.name} · ${p.health}/100 — open Object page`;
          return p.href ? (
            <Link
              key={p.entity.id}
              href={p.href}
              variant="unstyled"
              title={tip}
              className={cls}
            >
              {bar}
            </Link>
          ) : (
            <span key={p.entity.id} title={tip} className={cls}>
              {bar}
            </span>
          );
        })}
      </div>
      <div className="flex gap-2 px-5 pb-3">
        {perEntity.map((p) =>
          p.href ? (
            <Link
              key={p.entity.id}
              href={p.href}
              variant="unstyled"
              className="flex-1 truncate rounded-sm px-1 py-1 text-center type-subtext text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              {p.entity.name}
            </Link>
          ) : (
            <span
              key={p.entity.id}
              className="flex-1 truncate px-1 py-1 text-center type-subtext text-muted-foreground"
            >
              {p.entity.name}
            </span>
          ),
        )}
      </div>
    </>
  );
}

/** SINGLE-SUBJECT L1, TEMPORAL — a date-labeled breakdown is a time series
 *  (e.g. Throughput's per-day counts), so draw it as a chronological trend line
 *  (line + faint area + dashed target), not value-sorted bars. Mirrors the
 *  `HealthSparkline` visual language, banded to the touch's own target +
 *  carrying first/last date axis labels and per-point hover dots. */
const TREND_H = 140;

function ComponentTrend({
  series,
  target,
  chartBand,
}: {
  series: SeriesPoint[];
  target: number;
  chartBand: Band;
}) {
  const maxX = series.length - 1;
  const c = BAND_CHART[chartBand];
  const line = series.map((p, i) => `${i},${100 - p.health}`).join(" ");
  const areaPts = `0,100 ${line} ${maxX},100`;
  const first = series[0]!;
  const last = series[maxX]!;
  // The underlying count rides the component label ("2026-06-13 (1)") — pull it
  // off for the per-point tooltip so a dot reads "Jun 13, 2026 · 1".
  const countOf = (label: string): string | null => {
    const m = label.match(/\(([^)]*)\)\s*$/);
    return m ? m[1]! : null;
  };
  return (
    <div className="px-5 pb-4 pt-3">
      <div className="relative w-full" style={{ height: TREND_H }}>
        <svg
          viewBox={`0 0 ${maxX} 100`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          aria-hidden
        >
          <line
            x1="0"
            y1={100 - target}
            x2={maxX}
            y2={100 - target}
            stroke="currentColor"
            className="text-foreground/15"
            strokeDasharray="3 3"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <polygon points={areaPts} className={c.fill} />
          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            className={c.line}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {/* Per-point hover dots — absolutely placed (so they don't scale with the
            stretched viewBox) and revealed on hover, carrying the date + count. */}
        {series.map((p, i) => {
          const count = countOf(p.label);
          return (
            <span
              key={`${p.on}-${i}`}
              className="group/pt absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${(i / maxX) * 100}%`, top: `${100 - p.health}%` }}
              title={`${formatDate(p.on)}${count != null ? ` · ${count}` : ""}`}
            >
              <span
                className={cn(
                  "block size-1.5 rounded-full bg-current opacity-0 transition-opacity group-hover/pt:opacity-100",
                  c.line,
                )}
              />
            </span>
          );
        })}
        <span
          className="pointer-events-none absolute right-0 -translate-y-1/2 rounded-sm bg-card px-1 type-subtext tabular-nums text-muted-foreground/60"
          style={{ top: `${100 - target}%` }}
        >
          {target} target
        </span>
        <span
          className={cn(
            "pointer-events-none absolute right-0 -translate-y-1/2 rounded-sm bg-card px-1 type-subtext font-medium tabular-nums",
            c.line,
          )}
          style={{ top: `${Math.min(Math.max(100 - last.health, 8), 88)}%` }}
        >
          {last.health}
        </span>
      </div>
      <div className="mt-1.5 flex items-center justify-between type-subtext tabular-nums text-muted-foreground/70">
        <span>{formatDate(first.on)}</span>
        <span>{formatDate(last.on)}</span>
      </div>
    </div>
  );
}

/** SINGLE-SUBJECT L1 — the component checks behind the number (ported `_zoom`
 *  InstanceLevel steps/checks: label · banded track · value). */
function ComponentRows({
  components,
  target,
}: {
  components: { label: string; health: number }[];
  target: number;
}) {
  if (!components.length) {
    return (
      <div className="px-5 pb-5 pt-4 text-center type-subtext text-muted-foreground">
        No component is instrumented for this touchpoint.
      </div>
    );
  }
  return (
    <div className="px-5 pb-4 pt-3">
      <div className="-mx-1">
        {[...components]
          .sort((a, c) => a.health - c.health)
          // Key by index, not label: component labels are NOT unique (e.g. the
          // traceability/cadence breakdowns list raw commit subjects, and a repo
          // can carry dozens of commits sharing one subject), so `key={s.label}`
          // collided → React "two children with the same key" + omitted rows.
          .map((s, i) => {
            const sb = band(s.health, target);
            return (
              <div
                key={`${s.label}-${i}`}
                className="flex items-center gap-3 border-b border-border/40 px-1 py-1.5 first:pt-0 last:border-b-0"
              >
                <span className="w-32 shrink-0 truncate type-body">{s.label}</span>
                <span className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted/40">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{ width: `${s.health}%`, background: `rgb(${pulseToneRgb(s.health, target)})` }}
                  />
                </span>
                <span className={`w-7 text-right type-subtext tabular-nums ${BAND[sb].fg}`}>
                  {s.health}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}
